import React, { useEffect, useState, useRef } from "react";
import DepartmentSelect from "./DepartmentSelect";
import axiosInstance from "../../../utils/axiosInstance";

function DoctorModal({
  isModalOpen,
  setIsModalOpen,
  submitButtonText,
  title,
  onDoctorAdded,
}) {
  const [departments, setDepartments] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
  const userRole = userDetails.role;

  const [formData, setFormData] = useState({
    name: "",
    qualification: "",
    description: "",
    status: "active", // default
    departmentId: "",
    image: null, // file object
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch departments
        const deptResponse = await axiosInstance.get("/departments");
        if (deptResponse.data.success) {
          setDepartments(deptResponse.data.departments);
        }

        // Fetch branches if hospital_admin
        if (userRole === "hospital_admin") {
          const branchResponse = await axiosInstance.get("/hospitals/branches");
          const branchesData =
            branchResponse.data.branches || branchResponse.data || [];
          setBranches(branchesData);

          // Auto-select if only one branch
          if (branchesData.length === 1) {
            setSelectedBranches([branchesData[0].id]);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, [userRole]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({ ...prev, image: e.target.files[0] }));
  };

  const handleBranchToggle = (branchId) => {
    setSelectedBranches((prev) =>
      prev.includes(branchId)
        ? prev.filter((id) => id !== branchId)
        : [...prev, branchId]
    );
  };

  const getSelectedBranchNames = () => {
    if (selectedBranches.length === 0) return "Select branches";
    if (selectedBranches.length === branches.length)
      return "All branches selected";
    return `${selectedBranches.length} branch(es) selected`;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation for hospital_admin
    if (userRole === "hospital_admin" && selectedBranches.length === 0) {
      alert("Please select at least one branch");
      return;
    }

    const data = new FormData();
    data.append("name", formData.name);
    data.append("qualification", formData.qualification);
    data.append("description", formData.description);
    data.append("status", formData.status);
    data.append("departmentId", formData.departmentId);
    if (formData.image) {
      data.append("image", formData.image);
    }

    // Add selected branches for hospital_admin
    if (userRole === "hospital_admin") {
      data.append("branchIds", JSON.stringify(selectedBranches));
    }

    try {
      const response = await axiosInstance.post("/doctors/createDoctor", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data && (response.data.doctor || response.data.doctors)) {
        const message =
          userRole === "hospital_admin"
            ? `Doctor created successfully in ${selectedBranches.length} branch(es)!`
            : "Doctor submitted successfully!";
        alert(message);

        // 🔥 Format doctor so UI matches existing list
        const doctorData = response.data.doctor || response.data.doctors[0];
        const formattedDoctor = {
          ...doctorData,
          departmentName:
            departments.find((d) => d.id == doctorData.departmentId)?.title ||
            departments.find((d) => d.id == doctorData.departmentId)?.name ||
            "Unknown",
          image: doctorData.image
            ? `${process.env.REACT_APP_BASE_URL}/uploads/${doctorData.image
                .split("\\")
                .pop()}`
            : null,
        };

        if (onDoctorAdded) {
          onDoctorAdded(formattedDoctor);
        }

        setFormData({
          name: "",
          qualification: "",
          description: "",
          status: "active",
          departmentId: "",
          image: null,
        });
        setSelectedBranches([]);

        setIsModalOpen(false);
      } else {
        alert("Error submitting doctor.");
      }
    } catch (error) {
      console.error("Submission error:", error);
      alert(
        error.response?.data?.error || "Something went wrong. Check console."
      );
    }
  };

  const closeModal = () => setIsModalOpen(false);

  if (!isModalOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-3xl relative">
        <h2 className="text-xl font-semibold mb-4">{title}</h2>
        <form onSubmit={handleSubmit} encType="multipart/form-data">
          {/* Doctor Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-600">
              Doctor Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="mt-1 p-2 w-full border rounded-md"
              required
            />
          </div>

          {/* Qualification */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-600">
              Qualification
            </label>
            <input
              type="text"
              name="qualification"
              value={formData.qualification}
              onChange={handleInputChange}
              className="mt-1 p-2 w-full border rounded-md"
              required
            />
          </div>

          {/* Description */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-600">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="mt-1 p-2 w-full border rounded-md"
              rows="3"
            />
          </div>

          {/* Status */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-600">
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              className="mt-1 p-2 w-full border rounded-md"
              required
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Department */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-600">
              Department
            </label>
            <DepartmentSelect
              departments={departments}
              value={formData.departmentId}
              onChange={(e) =>
                setFormData({ ...formData, departmentId: e.target.value })
              }
            />
          </div>

          {/* Branch Selection for hospital_admin */}
          {userRole === "hospital_admin" && (
            <div className="mb-4" ref={dropdownRef}>
              <label className="block text-sm font-medium text-gray-600 mb-2">
                {branches.length === 1 ? "Branch" : "Select Branches *"}
              </label>

              {/* Single branch - show as text/readonly */}
              {branches.length === 1 ? (
                <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
                  {branches[0].name}
                </div>
              ) : (
                /* Multiple branches - show dropdown */
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-full px-3 py-2 text-left border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white flex items-center justify-between"
                  >
                    <span
                      className={
                        selectedBranches.length === 0
                          ? "text-gray-400"
                          : "text-gray-700"
                      }
                    >
                      {getSelectedBranchNames()}
                    </span>
                    <svg
                      className={`w-5 h-5 transition-transform ${
                        isDropdownOpen ? "transform rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {branches.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500">
                          No branches available
                        </div>
                      ) : (
                        <>
                          <div className="sticky top-0 bg-gray-50 px-3 py-2 border-b border-gray-200">
                            <button
                              type="button"
                              onClick={() => {
                                if (
                                  selectedBranches.length === branches.length
                                ) {
                                  setSelectedBranches([]);
                                } else {
                                  setSelectedBranches(
                                    branches.map((b) => b.id)
                                  );
                                }
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                            >
                              {selectedBranches.length === branches.length
                                ? "Deselect All"
                                : "Select All"}
                            </button>
                          </div>
                          {branches.map((branch) => (
                            <label
                              key={branch.id}
                              className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={selectedBranches.includes(branch.id)}
                                onChange={() => handleBranchToggle(branch.id)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <span className="ml-2 text-sm text-gray-700">
                                {branch.name}
                              </span>
                            </label>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Image Upload */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-600">
              Profile Image
            </label>
            <input
              type="file"
              name="image"
              accept="image/*"
              onChange={handleFileChange}
              className="mt-1 p-2 w-full border rounded-md"
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={closeModal}
              className="bg-gray-300 text-gray-700 py-2 px-4 rounded-md"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-blue-500 text-white py-2 px-4 rounded-md"
            >
              {submitButtonText}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DoctorModal;
